import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useDarkMode } from '../../../hooks/useDarkMode';

const DashboardHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isDark, setIsDark] = useDarkMode(); 
  
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  const [userData, setUserData] = useState({
    id: null,
    nomeCompleto: '...',
    ruolo: '...' 
  });

  const [unreadDocs, setUnreadDocs] = useState([]);

  // Refs za zatvaranje menija na klik izvan njih
  const settingsMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleLogoClick = () => {
    if (userData.ruolo === 'User') navigate('/acv-home');
    else if (userData.ruolo === 'Owner') navigate('/acv-home');
    else navigate('/acv-home'); 
  };

  const changeTheme = (themeName) => {
    const body = document.body;
    const themes = ['theme-green', 'theme-black', 'theme-purple', 'theme-orange'];
    themes.forEach(theme => body.classList.remove(theme));
    
    if (themeName !== 'default') {
      body.classList.add(`theme-${themeName}`);
      localStorage.setItem('selected-theme', `theme-${themeName}`);
    } else {
      localStorage.setItem('selected-theme', 'default');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) setShowSettingsMenu(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setShowUserMenu(false);
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setShowNotifications(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profilo } = await supabase
        .from('profili')
        .select('id, nome, cognome, ruolo')
        .eq('id', user.id)
        .single();

      if (profilo) {
        setUserData({
          id: profilo.id,
          nomeCompleto: `${profilo.nome} ${profilo.cognome}`,
          ruolo: profilo.ruolo || 'User'
        });

        const { data: docs } = await supabase
          .from('documenti')
          .select('*')
          .eq('id_utente', profilo.id)
          .eq('procitano', false)
          .order('created_at', { ascending: false });

        setUnreadDocs(docs || []);
      }
    } catch (err) {
      console.error("Error fetching header data:", err.message);
    }
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('header-notifications-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'documenti' }, () => fetchData())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error("Logout error:", err.message);
    }
  };

  return (
    <header className="header" role="banner">
      <div className="header-container relative">
        {/* LOGO */}
        <div className="header-logo cursor-pointer" onClick={handleLogoClick}>
          <div className="header-logo-icon">
            <div className="relative flex items-center justify-center">
              <Icon name="Shield" size={24} color="var(--color-primary)" strokeWidth={2} />
              <span className="absolute inset-0 flex items-center justify-center font-black text-primary select-none" style={{ fontSize: '11px', transform: 'translateY(-5%)', lineHeight: 1 }}>O</span>
            </div>
          </div>
          <span className="header-logo-text">OrizonDesk</span>
        </div>

        <nav className="header-actions flex items-center gap-2">
          
          {/* NOTIFIKACIJE */}
          <div className="relative" ref={notificationRef}>
            <button className={`header-action-button relative ${showNotifications ? 'bg-primary/10 text-primary' : ''}`} onClick={() => setShowNotifications(!showNotifications)}>
              <Icon name="Bell" size={20} />
              {unreadDocs.length > 0 && <span className="absolute top-2.5 right-2.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-background animate-pulse" />}
            </button>

            {showNotifications && (
              <div className="absolute top-12 right-0 bg-card rounded-xl shadow-2xl border border-border z-[1000] min-w-[300px] overflow-hidden">
                <div className="p-4 border-b border-border bg-muted/20">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground flex items-center justify-between">
                    {t('notifikacije.title_not', 'Notifiche')}
                    {unreadDocs.length > 0 && <span className="bg-red-500 text-white text-[9px] px-2 py-0.5 rounded-full">{unreadDocs.length}</span>}
                  </h3>
                </div>
                <div className="max-h-[350px] overflow-y-auto text-xs">
                  {unreadDocs.length > 0 ? (
                    unreadDocs.map((doc) => (
                      <button key={doc.id} onClick={() => { setShowNotifications(false); navigate('/my-documents'); }} className="w-full text-left p-4 hover:bg-primary/5 border-b border-border last:border-0 flex gap-3 group transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white shrink-0">
                          <Icon name="FileText" size={16} />
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <p className="text-[9px] font-black uppercase text-primary truncate">{doc.nome_file}</p>
                          <p className="text-[10px] text-muted-foreground italic">{formatDate(doc.created_at)}</p>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-8 text-center text-muted-foreground uppercase text-[10px] font-black tracking-widest italic opacity-50">
                      Nessuna nuova notifica
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="h-6 w-[1px] bg-border mx-1"></div>

          {/* POSTAVKE (TEMA & DARK MODE) */}
          <div className="relative" ref={settingsMenuRef}>
            <button onClick={() => setShowSettingsMenu(!showSettingsMenu)} className={`header-action-button ${showSettingsMenu ? 'bg-primary/10 text-primary' : ''}`}>
              <Icon name="Settings" size={20} />
            </button>
            {showSettingsMenu && (
              <div className="absolute top-12 right-0 bg-card rounded-xl shadow-xl border border-border z-[1000] min-w-[220px] p-4 overflow-hidden">
                <p className="text-[10px] font-black uppercase text-muted-foreground mb-3 tracking-widest flex items-center gap-2">
                  <Icon name="Palette" size={12} /> {t('settings.theme_title', 'Temi Sistema')}
                </p>
                <div className="grid grid-cols-5 gap-2 mb-4">
                  <button onClick={() => changeTheme('default')} className="w-6 h-6 rounded-full bg-[#1E3A5F] border border-border hover:scale-125 transition-transform" />
                  <button onClick={() => changeTheme('green')} className="w-6 h-6 rounded-full bg-[#059669] border border-border hover:scale-125 transition-transform" />
                  <button onClick={() => changeTheme('black')} className="w-6 h-6 rounded-full bg-[#111827] border border-border hover:scale-125 transition-transform" />
                  <button onClick={() => changeTheme('purple')} className="w-6 h-6 rounded-full bg-[#7C3AED] border border-border hover:scale-125 transition-transform" />
                  <button onClick={() => changeTheme('orange')} className="w-6 h-6 rounded-full bg-[#EA580C] border border-border hover:scale-125 transition-transform" />
                </div>
                <div className="h-[1px] bg-border w-full my-4" />
                <button onClick={() => setIsDark(!isDark)} className="w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-border">
                  <div className="flex items-center gap-2">
                    <Icon name={isDark ? "Sun" : "Moon"} size={16} className={isDark ? "text-yellow-500" : "text-primary"} />
                    <span className="text-[10px] font-bold uppercase tracking-wider">{isDark ? 'Light' : 'Dark'} Mode</span>
                  </div>
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${isDark ? 'bg-primary' : 'bg-gray-300'}`}>
                    <div className={`absolute top-1 bg-white w-2 h-2 rounded-full transition-transform ${isDark ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                </button>
              </div>
            )}
          </div>
         
          {/* USER MENU (BEZ PROFILA, SAMO LOGOUT) */}
          <div className="relative" ref={userMenuRef}>
            <button onClick={() => setShowUserMenu(!showUserMenu)} className={`header-action-button ${showUserMenu ? 'bg-primary/10 text-primary' : ''}`}>
              <Icon name="User" size={20} />
            </button>
            {showUserMenu && (
              <div className="absolute top-12 right-0 bg-card rounded-lg shadow-xl border border-border z-[999] min-w-[220px] overflow-hidden">
                <div className="p-4 border-b border-border bg-muted/20 text-center md:text-left">
                  <p className="text-xs font-black uppercase text-foreground">{userData.nomeCompleto}</p>
                  <p className="text-[9px] uppercase font-bold tracking-widest text-primary mt-1 opacity-70 italic">{userData.ruolo}</p>
                </div>
                <div className="p-1">
                  <button onClick={handleLogout} className="w-full flex items-center justify-center md:justify-start gap-3 px-4 py-3 rounded-md hover:bg-red-500/10 text-red-500 transition-all group">
                    <Icon name="LogOut" size={16} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-black uppercase tracking-widest">{t('header.logout', 'Disconnetti')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default DashboardHeader;